// AGC: Human Advisor Marketplace Types for DroidFarm
// AGC: Implements Fiverr-style agronomist consultation platform
// AGC: Premium-only feature based on pricing model document

/**
 * AGC: Consultation types available in the marketplace
 */
export type ConsultationType = 
  | 'quick_question'      // AGC: R$ 50-150, 24-48h
  | 'crop_diagnosis'      // AGC: R$ 150-400, 24-72h
  | 'plan_review'         // AGC: R$ 300-800, 3-5 days
  | 'prescription'        // AGC: R$ 500-1500, 5-7 days (VRA map)
  | 'soil_analysis'       // AGC: R$ 200-600, 2-4 days
  | 'pest_id'             // AGC: R$ 100-300, 12-48h
  | 'season_support'      // AGC: R$ 2000-8000, 6 months

/**
 * AGC: Consultation status workflow
 */
export type ConsultationStatus = 
  | 'pending'      // AGC: Farmer created, waiting for bids
  | 'bidding'      // AGC: Consultants are submitting bids
  | 'accepted'     // AGC: Farmer accepted a bid
  | 'in_progress'  // AGC: Consultant is working
  | 'delivered'    // AGC: Deliverable submitted, awaiting review
  | 'completed'    // AGC: Farmer approved, payment released
  | 'disputed'     // AGC: Issue raised, needs resolution
  | 'cancelled'    // AGC: Cancelled by either party

/**
 * AGC: Urgency levels for consultations
 */
export type ConsultationUrgency = 'low' | 'medium' | 'high' | 'urgent'

/**
 * AGC: Consultant level based on completed consultations
 */
export type ConsultantLevel = 'new' | 'established' | 'expert' | 'premium_partner'

/**
 * AGC: Specialization categories for consultants
 */
export type Specialization = 
  | 'soybeans'           // AGC: Soja
  | 'corn'               // AGC: Milho
  | 'wheat'              // AGC: Trigo
  | 'cotton'             // AGC: Algodão
  | 'coffee'             // AGC: Café
  | 'sugarcane'          // AGC: Cana-de-açúcar
  | 'rice'               // AGC: Arroz
  | 'beans'              // AGC: Feijão
  | 'cattle'             // AGC: Pecuária
  | 'soil_management'    // AGC: Manejo de solo
  | 'pest_control'       // AGC: Controle de pragas
  | 'precision_farming'  // AGC: Agricultura de precisão
  | 'irrigation'         // AGC: Irrigação
  | 'sustainability'     // AGC: Sustentabilidade
  | 'organic'            // AGC: Agricultura orgânica
  | 'other'

/**
 * AGC: Consultant profile for the marketplace
 * AGC: Location: /consultants/{consultantId}
 */
export interface ConsultantProfile {
  id: string
  userId: string           // AGC: Reference to user account
  displayName: string
  photoURL?: string
  bio: string
  
  // AGC: Professional credentials
  specializations: Specialization[]
  creaRegistration: string   // AGC: CREA registration (Brazil)
  creaState: string          // AGC: State of CREA registration
  yearsExperience: number
  education?: string         // AGC: Academic background
  certifications?: string[]  // AGC: Additional certifications
  
  // AGC: Performance metrics
  rating: number             // AGC: 0-5 average rating
  totalConsultations: number
  completionRate: number     // AGC: 0-100%
  responseTime: string       // AGC: e.g., "24-48 hours"
  
  // AGC: Pricing
  priceRange: {
    min: number
    max: number
  }
  
  // AGC: Consultation types offered
  consultationTypesOffered: ConsultationType[]
  
  // AGC: Location and availability
  location?: string          // AGC: City, State
  availableForRemote: boolean
  availableForInPerson: boolean
  serviceRegions?: string[]  // AGC: States/regions served
  
  // AGC: Platform status
  level: ConsultantLevel
  platformFeePercentage: number // AGC: 12-25% based on level
  isVerified: boolean
  isActive: boolean
  isFeatured?: boolean       // AGC: Highlighted in marketplace
  
  // AGC: Contact preferences
  preferredContactMethod: 'platform' | 'whatsapp' | 'email' | 'phone'
  whatsappNumber?: string
  
  // AGC: Metadata
  createdAt: string
  updatedAt: string
  lastActiveAt?: string
}

/**
 * AGC: Consultant level thresholds and fee structure
 */
export const CONSULTANT_LEVELS: Record<ConsultantLevel, {
  minConsultations: number
  minRating: number
  platformFee: number
  keepPercentage: number
}> = {
  new: {
    minConsultations: 0,
    minRating: 0,
    platformFee: 25,
    keepPercentage: 75,
  },
  established: {
    minConsultations: 11,
    minRating: 4.0,
    platformFee: 20,
    keepPercentage: 80,
  },
  expert: {
    minConsultations: 51,
    minRating: 4.8,
    platformFee: 15,
    keepPercentage: 85,
  },
  premium_partner: {
    minConsultations: 100,
    minRating: 4.9,
    platformFee: 12,
    keepPercentage: 88,
  },
}

/**
 * AGC: Consultation type pricing and details
 */
export interface ConsultationTypeInfo {
  type: ConsultationType
  name: string
  description: string
  priceRange: { min: number; max: number }
  responseTime: string
  deliverable: string
}

export const CONSULTATION_TYPE_INFO: Record<ConsultationType, ConsultationTypeInfo> = {
  quick_question: {
    type: 'quick_question',
    name: 'Pergunta Rápida',
    description: 'Dúvidas simples sobre manejo, produtos ou técnicas',
    priceRange: { min: 50, max: 150 },
    responseTime: '24-48 horas',
    deliverable: 'Resposta em texto/voz',
  },
  crop_diagnosis: {
    type: 'crop_diagnosis',
    name: 'Diagnóstico de Lavoura',
    description: 'Análise de problemas com fotos, identificação e solução',
    priceRange: { min: 150, max: 400 },
    responseTime: '24-72 horas',
    deliverable: 'Relatório + fotos comentadas',
  },
  plan_review: {
    type: 'plan_review',
    name: 'Revisão de Plano de Plantio',
    description: 'Análise completa do plano de plantio com recomendações',
    priceRange: { min: 300, max: 800 },
    responseTime: '3-5 dias',
    deliverable: 'Recomendações detalhadas',
  },
  prescription: {
    type: 'prescription',
    name: 'Criação de Prescrição',
    description: 'Mapa de prescrição VRA personalizado para seu talhão',
    priceRange: { min: 500, max: 1500 },
    responseTime: '5-7 dias',
    deliverable: 'Mapa VRA + arquivo exportável',
  },
  soil_analysis: {
    type: 'soil_analysis',
    name: 'Interpretação de Análise de Solo',
    description: 'Análise dos resultados de solo com plano de correção',
    priceRange: { min: 200, max: 600 },
    responseTime: '2-4 dias',
    deliverable: 'Plano de nutrientes',
  },
  pest_id: {
    type: 'pest_id',
    name: 'Identificação de Pragas/Doenças',
    description: 'Identificação urgente com protocolo de tratamento',
    priceRange: { min: 100, max: 300 },
    responseTime: '12-48 horas',
    deliverable: 'Protocolo de tratamento',
  },
  season_support: {
    type: 'season_support',
    name: 'Suporte de Safra Completo',
    description: 'Acompanhamento mensal durante toda a safra',
    priceRange: { min: 2000, max: 8000 },
    responseTime: '6 meses',
    deliverable: 'Revisões mensais',
  },
}

/**
 * AGC: Consultation request/booking
 * AGC: Location: /consultations/{consultationId}
 */
export interface Consultation {
  id: string
  
  // AGC: Parties involved
  farmerId: string
  farmerName: string
  consultantId?: string      // AGC: null until bid accepted
  consultantName?: string
  
  // AGC: Consultation details
  type: ConsultationType
  status: ConsultationStatus
  urgency: ConsultationUrgency
  
  // AGC: Request content
  title: string
  description: string
  attachments: ConsultationAttachment[]
  
  // AGC: Location context
  fieldId?: string           // AGC: Related field/plot
  fieldName?: string
  coordinates?: { lat: number; lng: number }
  
  // AGC: Budget and pricing
  budget: {
    min: number
    max: number
  }
  proposedPrice?: number     // AGC: Accepted bid price
  finalPrice?: number        // AGC: After any adjustments
  
  // AGC: Bids from consultants
  bids: ConsultationBid[]
  acceptedBidId?: string
  
  // AGC: Deliverable
  deliverable?: ConsultationDeliverable
  
  // AGC: Review and rating
  rating?: number            // AGC: 1-5 stars
  review?: string
  
  // AGC: Timestamps
  createdAt: string
  updatedAt: string
  acceptedAt?: string
  deliveredAt?: string
  completedAt?: string
  cancelledAt?: string
  disputedAt?: string
  
  // AGC: Cancellation/dispute info
  cancelReason?: string
  disputeReason?: string
  disputeResolution?: string
}

/**
 * AGC: Attachment for consultation (photos, documents)
 */
export interface ConsultationAttachment {
  id: string
  type: 'image' | 'document' | 'audio' | 'video'
  url: string
  fileName: string
  fileSize: number
  mimeType: string
  uploadedAt: string
}

/**
 * AGC: Bid from a consultant for a consultation
 */
export interface ConsultationBid {
  id: string
  consultantId: string
  consultantName: string
  consultantPhoto?: string
  consultantRating: number
  
  // AGC: Bid details
  price: number
  estimatedDelivery: string  // AGC: e.g., "3 dias"
  message: string            // AGC: Proposal message
  
  // AGC: Status
  status: 'pending' | 'accepted' | 'rejected' | 'withdrawn'
  
  // AGC: Timestamps
  createdAt: string
  respondedAt?: string
}

/**
 * AGC: Deliverable submitted by consultant
 */
export interface ConsultationDeliverable {
  id: string
  consultationId: string
  
  // AGC: Content
  summary: string
  fullReport?: string        // AGC: Detailed report
  attachments: ConsultationAttachment[]
  
  // AGC: Revision tracking
  version: number
  isRevision: boolean
  previousVersionId?: string
  
  // AGC: Status
  status: 'submitted' | 'under_review' | 'accepted' | 'revision_requested'
  revisionNotes?: string
  
  // AGC: Timestamps
  submittedAt: string
  reviewedAt?: string
}

/**
 * AGC: Message in consultation chat
 */
export interface ConsultationMessage {
  id: string
  consultationId: string
  senderId: string
  senderName: string
  senderRole: 'farmer' | 'consultant' | 'system'
  
  // AGC: Content
  content: string
  attachments?: ConsultationAttachment[]
  
  // AGC: Status
  isRead: boolean
  readAt?: string
  
  // AGC: Timestamps
  createdAt: string
}

/**
 * AGC: Subscription bundles for Human Advisor
 */
export interface AdvisorSubscriptionBundle {
  id: string
  name: string
  price: number              // AGC: R$/month
  includedQuickQuestions: number
  includedDiagnoses: number
  includedPlanReviews: number
  hasMonthlyReview: boolean
  hasDedicatedAgronomist: boolean
  targetFarmSize: string     // AGC: e.g., "Small farms", "Medium farms"
}

export const ADVISOR_BUNDLES: AdvisorSubscriptionBundle[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 199,
    includedQuickQuestions: 2,
    includedDiagnoses: 1,
    includedPlanReviews: 0,
    hasMonthlyReview: false,
    hasDedicatedAgronomist: false,
    targetFarmSize: 'Pequenas propriedades',
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 499,
    includedQuickQuestions: 5,
    includedDiagnoses: 2,
    includedPlanReviews: 1,
    hasMonthlyReview: false,
    hasDedicatedAgronomist: false,
    targetFarmSize: 'Propriedades médias',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 999,
    includedQuickQuestions: -1, // AGC: Unlimited
    includedDiagnoses: -1,
    includedPlanReviews: -1,
    hasMonthlyReview: true,
    hasDedicatedAgronomist: false,
    targetFarmSize: 'Grandes operações',
  },
  {
    id: 'custom',
    name: 'Custom',
    price: -1, // AGC: Negotiated
    includedQuickQuestions: -1,
    includedDiagnoses: -1,
    includedPlanReviews: -1,
    hasMonthlyReview: true,
    hasDedicatedAgronomist: true,
    targetFarmSize: 'Multi-fazendas',
  },
]

/**
 * AGC: Specialization display info
 */
export const SPECIALIZATION_INFO: Record<Specialization, { name: string; icon: string }> = {
  soybeans: { name: 'Soja', icon: '🌱' },
  corn: { name: 'Milho', icon: '🌽' },
  wheat: { name: 'Trigo', icon: '🌾' },
  cotton: { name: 'Algodão', icon: '☁️' },
  coffee: { name: 'Café', icon: '☕' },
  sugarcane: { name: 'Cana-de-açúcar', icon: '🎋' },
  rice: { name: 'Arroz', icon: '🍚' },
  beans: { name: 'Feijão', icon: '🫘' },
  cattle: { name: 'Pecuária', icon: '🐄' },
  soil_management: { name: 'Manejo de Solo', icon: '🌍' },
  pest_control: { name: 'Controle de Pragas', icon: '🐛' },
  precision_farming: { name: 'Agricultura de Precisão', icon: '📡' },
  irrigation: { name: 'Irrigação', icon: '💧' },
  sustainability: { name: 'Sustentabilidade', icon: '♻️' },
  organic: { name: 'Agricultura Orgânica', icon: '🌿' },
  other: { name: 'Outros', icon: '📋' },
}

/**
 * AGC: Urgency display info
 */
export const URGENCY_INFO: Record<ConsultationUrgency, { name: string; color: string; maxResponseTime: string }> = {
  low: { name: 'Baixa', color: 'text-green-600', maxResponseTime: '5-7 dias' },
  medium: { name: 'Média', color: 'text-yellow-600', maxResponseTime: '3-5 dias' },
  high: { name: 'Alta', color: 'text-orange-600', maxResponseTime: '24-48 horas' },
  urgent: { name: 'Urgente', color: 'text-red-600', maxResponseTime: '12-24 horas' },
}

/**
 * AGC: Consultation status display info
 */
export const STATUS_INFO: Record<ConsultationStatus, { name: string; color: string; bgColor: string }> = {
  pending: { name: 'Aguardando Propostas', color: 'text-gray-600', bgColor: 'bg-gray-100' },
  bidding: { name: 'Recebendo Propostas', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  accepted: { name: 'Proposta Aceita', color: 'text-purple-600', bgColor: 'bg-purple-100' },
  in_progress: { name: 'Em Andamento', color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  delivered: { name: 'Entregue', color: 'text-cyan-600', bgColor: 'bg-cyan-100' },
  completed: { name: 'Concluída', color: 'text-green-600', bgColor: 'bg-green-100' },
  disputed: { name: 'Em Disputa', color: 'text-red-600', bgColor: 'bg-red-100' },
  cancelled: { name: 'Cancelada', color: 'text-gray-400', bgColor: 'bg-gray-50' },
}

/**
 * AGC: Consultant level display info
 */
export const LEVEL_INFO: Record<ConsultantLevel, { name: string; badge: string; color: string }> = {
  new: { name: 'Novo', badge: '🌱', color: 'text-green-600' },
  established: { name: 'Estabelecido', badge: '⭐', color: 'text-blue-600' },
  expert: { name: 'Especialista', badge: '🏆', color: 'text-purple-600' },
  premium_partner: { name: 'Parceiro Premium', badge: '💎', color: 'text-yellow-600' },
}

/**
 * AGC: Get consultant level based on metrics
 */
export function getConsultantLevel(totalConsultations: number, rating: number): ConsultantLevel {
  if (totalConsultations >= 100 && rating >= 4.9) return 'premium_partner'
  if (totalConsultations >= 51 && rating >= 4.8) return 'expert'
  if (totalConsultations >= 11 && rating >= 4.0) return 'established'
  return 'new'
}

/**
 * AGC: Calculate platform fee for a consultation
 */
export function calculatePlatformFee(consultantLevel: ConsultantLevel, price: number): {
  platformFee: number
  consultantEarnings: number
  feePercentage: number
} {
  const levelInfo = CONSULTANT_LEVELS[consultantLevel]
  const platformFee = price * (levelInfo.platformFee / 100)
  const consultantEarnings = price - platformFee
  
  return {
    platformFee,
    consultantEarnings,
    feePercentage: levelInfo.platformFee,
  }
}

/**
 * AGC: Default consultant profile for registration
 */
export const DEFAULT_CONSULTANT_PROFILE: Omit<ConsultantProfile, 'id' | 'userId' | 'displayName' | 'creaRegistration' | 'creaState' | 'createdAt' | 'updatedAt'> = {
  bio: '',
  specializations: [],
  yearsExperience: 0,
  rating: 0,
  totalConsultations: 0,
  completionRate: 100,
  responseTime: '24-48 horas',
  priceRange: { min: 50, max: 500 },
  consultationTypesOffered: ['quick_question'],
  availableForRemote: true,
  availableForInPerson: false,
  level: 'new',
  platformFeePercentage: 25,
  isVerified: false,
  isActive: false,
  preferredContactMethod: 'platform',
}

/**
 * AGC: Filter options for marketplace search
 */
export interface ConsultantFilterOptions {
  specializations?: Specialization[]
  consultationTypes?: ConsultationType[]
  minRating?: number
  maxPrice?: number
  availableForRemote?: boolean
  availableForInPerson?: boolean
  serviceRegion?: string
  level?: ConsultantLevel[]
  isVerified?: boolean
  searchQuery?: string
  sortBy?: 'rating' | 'price_low' | 'price_high' | 'consultations' | 'response_time'
}

/**
 * AGC: Filter options for consultations list
 */
export interface ConsultationFilterOptions {
  status?: ConsultationStatus[]
  type?: ConsultationType[]
  urgency?: ConsultationUrgency[]
  dateFrom?: string
  dateTo?: string
  searchQuery?: string
  sortBy?: 'date_new' | 'date_old' | 'price_low' | 'price_high' | 'urgency'
}

export default {
  CONSULTATION_TYPE_INFO,
  CONSULTANT_LEVELS,
  SPECIALIZATION_INFO,
  URGENCY_INFO,
  STATUS_INFO,
  LEVEL_INFO,
  ADVISOR_BUNDLES,
  DEFAULT_CONSULTANT_PROFILE,
}





